/* Maison Byōbu · les molettes du cadreur (mobile)
   Sous les champs, « Régler à la molette » ouvre un panneau bas (<dialog> modal) : pour la largeur et pour la
   hauteur, une molette des mètres et une des centimètres. Les champs texte restent la source de vérité : le
   panneau part de leurs valeurs et n'y écrit qu'au bouton « Cadrer avec ces mesures » (cadreur.poserMesures).
   « Annuler » ou Échap ferment sans rien changer ; toucher le fond ne ferme pas (un réglage n'est jamais perdu par erreur).
   Molettes dessinées : sur iPhone, un <select> ouvre un menu, jamais une roue (WebKit, depuis iOS 15).
   Pointer events et transform seulement, pas de scroll-snap (élan, scrollend et recalage instables avant iOS 27).
   Une seule boucle requestAnimationFrame, arrêtée dès que tout est posé. Tout est dans cette fonction :
   rien au niveau global (MB_BORNES, mbEuros… appartiennent à mb-cadreur.js). Spécification : docs/molettes.md. */
(() => {
  if (customElements.get('mb-molettes')) return;

  /* La sensation, à régler sur l'iPhone de Marcel */
  const R = {
    elanMs: 280,           // portée de l'élan : vitesse du doigt au lâcher × 280 ms
    ressortElan: 2 / 280,  // ω du ressort après un lancer (par ms)
    ressortPose: 1 / 70,   // ω du ressort sans élan (toucher, clavier, recalage)
    tolerance: 5,          // px avant qu'un toucher devienne un glisser
    fenetreVitesse: 100,   // ms de traces pour mesurer la vitesse au lâcher
    debord: 1.2,           // au plus 1,2 rang au-delà du bord permis
    raideur: 0.55,         // résistance du bord
  };
  const NBSP = ' ';
  const entier = new Intl.NumberFormat('fr-FR');
  const reduit = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pluriel = (n) => (n > 1 ? 's' : '');
  const mesure = (v) => (Number.isInteger(v) ? v : null); // une saisie refusée (NaN) n'est pas une mesure choisie

  /* Une seule boucle pour les quatre molettes, arrêtée quand plus rien ne bouge */
  const boucle = {
    actives: new Set(),
    prevu: 0,
    ajouter(m) {
      this.actives.add(m);
      if (!this.prevu) this.prevu = requestAnimationFrame((t) => this.tic(t));
    },
    tic(t) {
      this.prevu = 0;
      for (const m of this.actives) if (!m.avancer(t)) this.actives.delete(m);
      if (this.actives.size) this.prevu = requestAnimationFrame((t2) => this.tic(t2));
    },
    vider() {
      this.actives.clear();
      if (this.prevu) cancelAnimationFrame(this.prevu);
      this.prevu = 0;
    },
  };

  class Molette {
    /* el : la molette ; n : nombre de rangs (0 à n − 1) ; rappel(molette, { visee, choix, bute }) */
    constructor(el, n, rappel) {
      this.el = el; this.n = n; this.rappel = rappel;
      this.lo = 0; this.hi = n - 1;
      this.pos = 0; this.cible = 0; this.suivie = 0;
      this.voulu = 0; // dernier choix du client : gardé pendant un détour par 15 m (ou 4 m, ou 0 m)
      this.h = 44; this.anim = null; this.doigt = null;
      const fenetre = document.createElement('div');
      fenetre.className = 'mb-molette__fenetre';
      fenetre.setAttribute('aria-hidden', 'true');
      this.bande = document.createElement('div');
      this.bande.className = 'mb-molette__bande';
      const chiffres = el.dataset.unite === 'cm' ? 2 : 1;
      for (let k = 0; k < n; k++) {
        const r = document.createElement('span');
        r.className = 'mb-molette__rang';
        r.textContent = String(k).padStart(chiffres, '0');
        this.bande.append(r);
      }
      fenetre.append(this.bande);
      el.append(fenetre);
      this.rangs = [...this.bande.children];
      this.gestes();
    }

    /* Pendant un glisser : le rang sous la lentille ; sinon : le rang visé, connu dès le lâcher */
    get valeur() { return this.doigt ? this.borne(this.pos) : this.cible; }
    borne(k) { return Math.min(Math.max(Math.round(k), this.lo), this.hi); }
    mesurer() { this.h = this.rangs[0].offsetHeight || this.h; this.peindre(); }
    peindre() { this.bande.style.transform = `translate3d(0, ${(-this.pos * this.h).toFixed(2)}px, 0)`; }
    aria() {
      this.el.setAttribute('aria-valuemin', String(this.lo));
      this.el.setAttribute('aria-valuemax', String(this.hi));
      this.el.setAttribute('aria-valuenow', String(this.cible));
    }

    /* Pose sans mouvement (ouverture, recalage sans animation) */
    fixer(k) { this.anim = null; this.pos = this.cible = this.suivie = this.borne(k); this.peindre(); this.aria(); }

    /* Plage permise ; les rangs hors plage restent visibles, pâlis, comme sur la roue de date de l'iPhone */
    limiter(lo, hi) {
      this.lo = lo; this.hi = hi;
      this.rangs.forEach((r, k) => r.classList.toggle('mb-hors', k < lo || k > hi));
      this.aria();
    }

    /* Va vers le rang k, toujours borné. v : vitesse du doigt au lâcher (rangs par ms), gardée par un ressort
       amorti critique : ni dépassement ni à-coup. choix : geste ou touche du client. */
    aller(k, { v = 0, choix = false } = {}) {
      const cible = this.borne(k);
      const bute = choix && Math.round(k) > this.hi; // le client demandait plus que permis
      if (choix) this.voulu = cible;
      this.cible = this.suivie = cible;
      if (reduit()) { this.anim = null; this.pos = cible; this.peindre(); }
      else {
        const w = v ? R.ressortElan : R.ressortPose;
        const A = this.pos - cible;
        this.anim = { t0: performance.now(), A, B: v + w * A, w };
        boucle.ajouter(this);
      }
      this.aria();
      this.rappel(this, { visee: true, choix, bute });
    }

    avancer(t) { // x(t) = (A + B t) e^(−w t)
      if (this.doigt) { this.peindre(); return false; }
      const a = this.anim;
      if (!a) return false;
      const dt = Math.max(0, t - a.t0);
      const e = Math.exp(-a.w * dt);
      const x = (a.A + a.B * dt) * e;
      const v = (a.B - a.w * (a.A + a.B * dt)) * e;
      if (Math.abs(x) < 0.002 && Math.abs(v) < 0.0005) { this.anim = null; this.pos = this.cible; this.peindre(); return false; }
      this.pos = this.cible + x;
      this.peindre();
      return true;
    }

    /* Au-delà de la plage permise, la molette résiste (au plus 1,2 rang) puis revient */
    elastique(p) {
      const ressort = (d) => R.debord * (1 - 1 / (1 + (d * R.raideur) / R.debord));
      if (p < this.lo) return this.lo - ressort(this.lo - p);
      if (p > this.hi) return this.hi + ressort(p - this.hi);
      return p;
    }

    /* Un doigt encore posé (validation de l'autre main, fermeture) : pose sur le rang le plus proche, sans élan */
    lacherTout() {
      const d = this.doigt;
      if (!d) return;
      this.doigt = null;
      this.el.classList.remove('mb-actif');
      try { this.el.releasePointerCapture(d.id); } catch (_) { /* déjà relâché */ }
      this.aller(Math.round(this.pos), { choix: d.bouge });
    }

    gestes() {
      const el = this.el;
      el.addEventListener('pointerdown', (e) => {
        if (e.button > 0 || this.doigt) return;
        try { el.setPointerCapture(e.pointerId); } catch (_) { /* événement de test */ }
        // Attraper, seulement si la molette tourne encore (au moins un demi-rang de sa place) : la fin du ressort de pose
        // dure ~600 ms, et deux touchers rapides sous la lentille doivent compter pour deux
        const attrape = !!this.anim && Math.abs(this.pos - this.cible) >= 0.5;
        this.anim = null; // on attrape la molette en plein élan, là où elle est
        this.doigt = { id: e.pointerId, y: e.clientY, pos: this.pos, bouge: false, attrape, traces: [[e.timeStamp, this.pos]] };
        el.classList.add('mb-actif');
      });
      el.addEventListener('pointermove', (e) => {
        const d = this.doigt;
        if (!d || e.pointerId !== d.id) return;
        const dy = e.clientY - d.y;
        if (!d.bouge) {
          if (Math.abs(dy) < R.tolerance) return; // un toucher n'est pas un glisser
          d.bouge = true;
          this.rappel(this, { choix: true });
        }
        this.pos = this.elastique(d.pos - dy / this.h);
        d.traces.push([e.timeStamp, this.pos]);
        if (d.traces.length > 10) d.traces.shift();
        const k = this.borne(this.pos);
        if (k !== this.suivie) { this.suivie = k; this.el.setAttribute('aria-valuenow', String(k)); this.rappel(this, {}); } // aller() y remet la cible au lâcher
        boucle.ajouter(this);
      });
      const lacher = (e, annule) => {
        const d = this.doigt;
        if (!d || e.pointerId !== d.id) return;
        this.doigt = null;
        el.classList.remove('mb-actif');
        if (!d.bouge) {
          if (annule) return this.aller(Math.round(this.pos));
          if (d.attrape) return this.aller(Math.round(this.pos), { choix: true }); // toucher une molette qui tourne l'arrête
          const b = el.getBoundingClientRect(); // toucher un rang visible : la molette y va ; le rang central adopte la valeur
          return this.aller(this.cible + Math.round((e.clientY - (b.top + b.height / 2)) / this.h), { choix: true });
        }
        let v = 0; // vitesse sur les 100 dernières ms, en rangs par ms
        const recents = d.traces.filter(([t]) => e.timeStamp - t <= R.fenetreVitesse);
        if (!annule && !reduit() && recents.length > 1) {
          const [t0, p0] = recents[0];
          const [t1, p1] = recents[recents.length - 1];
          if (t1 - t0 > 10) v = (p1 - p0) / (t1 - t0);
        }
        this.aller(Math.round(this.pos + v * R.elanMs), { v, choix: true });
      };
      el.addEventListener('pointerup', (e) => lacher(e, false));
      el.addEventListener('pointercancel', (e) => lacher(e, true));      // geste système d'iOS, loupe
      el.addEventListener('lostpointercapture', (e) => lacher(e, true));
      /* Clavier et VoiceOver : les quatre flèches (la touche simulée par Safari pour un spinbutton n'est pas documentée) */
      el.addEventListener('keydown', (e) => {
        const pas = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1, PageUp: 10, PageDown: -10 }[e.key];
        let k;
        if (pas) k = this.cible + pas;
        else if (e.key === 'Home') k = this.lo;
        else if (e.key === 'End') k = this.hi;
        else return;
        e.preventDefault();
        this.aller(k, { choix: true });
      });
    }
  }

  class MbMolettes extends HTMLElement {
    connectedCallback() {
      if (this.pret) return;
      this.pret = true;
      this.dialog = this.querySelector('dialog');
      this.bouton = this.querySelector('[data-mb-molettes-ouvrir]');
      this.cadreur = this.closest('mb-cadreur');
      if (!this.dialog || !this.bouton || !this.cadreur || typeof this.dialog.showModal !== 'function') return; // bouton caché : le clavier suffit
      customElements.whenDefined('mb-cadreur').then(() => {
        const c = this.cadreur;
        if (!c.bornes || !['tarif', 'mesuresSaisies', 'poserMesures', 'texteHautMax'].every((f) => typeof c[f] === 'function')) return;
        this.brancher();
        this.dataset.pret = ''; // le CSS ne montre le bouton qu'à partir d'ici
      });
    }

    brancher() {
      const q = (s) => this.querySelector(s);
      this.el = {
        titre: q('[data-mb-molettes-titre]'), corps: q('.mb-molettes__corps'),
        prix: q('[data-mb-molettes-prix]'), ligne: q('[data-mb-molettes-ligne]'), limite: q('[data-mb-molettes-limite]'),
        dims: { L: q('[data-mb-molettes-dim="L"]'), H: q('[data-mb-molettes-dim="H"]') },
      };
      this.choisi = { L: false, H: false };
      this.depasse = false; this.buteH = false;
      this.bouton.addEventListener('click', () => this.ouvrir());
      q('[data-mb-molettes-fermer]').addEventListener('click', () => this.dialog.close());
      q('[data-mb-molettes-appliquer]').addEventListener('click', () => this.appliquer());
      this.dialog.addEventListener('close', () => this.apresFermeture());
      /* Fermé au doigt ou à la souris : pas d'anneau d'or sur le bouton au retour. WebKit garde :focus-visible d'un focus
         par script au suivant (titre à l'ouverture, bouton au retour) ; la classe éteint l'anneau jusqu'à la prochaine touche. */
      this.auDoigt = false;
      this.dialog.addEventListener('pointerdown', () => { this.auDoigt = true; }, true);
      this.dialog.addEventListener('keydown', () => { this.auDoigt = false; }, true);
      const rallumer = () => this.bouton.classList.remove('mb-au-doigt');
      this.bouton.addEventListener('blur', rallumer);
      this.bouton.addEventListener('keydown', rallumer);
      this.ro = 'ResizeObserver' in window ? new ResizeObserver(() => this.ajuster()) : null;
    }

    toutes() { return this.roues ? [this.roues.L.m, this.roues.L.cm, this.roues.H.m, this.roues.H.cm] : []; }

    construire() {
      if (this.roues) return;
      const B = this.cadreur.bornes;
      this.limites = { L: [B.lMin, B.lMax], H: [B.hMin, B.hMax] };
      this.roues = {};
      for (const d of ['L', 'H']) {
        const rappel = (mol, o) => this.surChange(d, mol, o);
        this.roues[d] = {
          m: new Molette(this.querySelector(`[data-mb-molette="${d}-m"]`), Math.floor(this.limites[d][1] / 100) + 1, rappel), // 0..15 ou 0..4
          cm: new Molette(this.querySelector(`[data-mb-molette="${d}-cm"]`), 100, rappel),                                  // 00..99
        };
      }
    }

    surChange(d, mol, { visee = false, choix = false, bute = false } = {}) {
      if (choix && !this.choisi[d]) { this.choisi[d] = true; this.el.dims[d].dataset.etat = 'choisi'; }
      if (choix && visee && d === 'H') { this.buteH = bute; this.depasse = false; }
      if (visee && mol === this.roues[d].m) this.restreindre(d, true);
      this.majTotal();
    }

    /* Le total d'une dimension, en centimètres entiers, toujours dans les bornes : la seule valeur jamais lue */
    total(d) {
      const { m, cm } = this.roues[d];
      const [min, max] = this.limites[d];
      return Math.min(Math.max(100 * m.valeur + cm.valeur, min), max);
    }

    poser(d, total) {
      const { m, cm } = this.roues[d];
      const [min, max] = this.limites[d];
      m.limiter(Math.floor(min / 100), Math.floor(max / 100));
      m.fixer(Math.floor(total / 100));
      this.restreindre(d, false);
      cm.fixer(total % 100);
      cm.voulu = total % 100;
    }

    /* 0 m : au moins 50 cm ; au maximum (15 m, 4 m) : 00 cm. La molette des centimètres glisse vers la valeur
       permise la plus proche des centimètres voulus, et les retrouve quand on redescend. */
    restreindre(d, anime) {
      const { m, cm } = this.roues[d];
      const [min, max] = this.limites[d];
      const base = 100 * m.cible;
      const lo = Math.max(0, min - base);
      const hi = Math.min(99, max - base);
      cm.limiter(lo, hi);
      const k = Math.min(Math.max(cm.voulu, lo), hi);
      if (k === cm.cible) return;
      if (anime) cm.aller(k); else cm.fixer(k);
    }

    majTotal() {
      if (!this.roues) return;
      const L = this.total('L');
      const H = this.total('H');
      if (this.choisi.L && this.choisi.H) {
        const t = this.cadreur.tarif(L, H);
        this.el.prix.textContent = t.prix;
        this.el.ligne.textContent = `${entier.format(L)}${NBSP}×${NBSP}${entier.format(H)}${NBSP}cm · ${t.surface}`;
      } else {
        this.el.prix.textContent = '—';
        this.el.ligne.textContent = 'Le prix s’affiche dès vos dimensions';
      }
      /* Au-delà de 4 m : seulement si le client a demandé plus (valeur tapée, ou geste qui a buté), jamais à 4 m choisi */
      const auDela = H === this.limites.H[1] && (this.depasse || this.buteH);
      if (auDela === this.el.limite.hidden) {
        this.el.limite.innerHTML = auDela ? this.cadreur.texteHautMax() : '';
        this.el.limite.hidden = !auDela;
        if (auDela) this.montrerLimite();
      }
      const limite = auDela ? ` ${this.el.limite.textContent.trim()}` : ''; // VoiceOver l'entend au moment où le client bute
      for (const [d, tot, mot] of [['L', L, 'largeur'], ['H', H, 'hauteur']]) {
        const { m, cm } = this.roues[d];
        const fin = `${mot} ${tot} centimètres${this.choisi[d] ? '' : ', exemple'}${d === 'H' && limite ? `.${limite}` : ''}`;
        m.el.setAttribute('aria-valuetext', `${m.valeur} mètre${pluriel(m.valeur)}, ${fin}`);
        cm.el.setAttribute('aria-valuetext', `${cm.valeur} centimètre${pluriel(cm.valeur)}, ${fin}`);
      }
    }

    /* Écran bas (couché, SE) : la ligne des 4 m apparaît sous le pli du corps ; on la fait monter juste assez,
       seulement quand elle apparaît (jamais pendant un glisser), sans toucher au défilement de la page */
    montrerLimite() {
      if (!this.dialog.open) return;
      const c = this.el.corps;
      if (c.scrollHeight <= c.clientHeight + 1) return;
      const exces = this.el.limite.getBoundingClientRect().bottom - (c.getBoundingClientRect().bottom - parseFloat(getComputedStyle(c).paddingBottom));
      if (exces > 0) c.scrollTop += Math.ceil(exces);
    }

    /* Hauteur des rangs (portrait, couché) et défilement du corps seulement s'il dépasse (texte agrandi, écran bas) */
    ajuster() {
      if (!this.dialog.open) return;
      this.toutes().forEach((m) => m.mesurer());
      const c = this.el.corps;
      this.dialog.classList.toggle('mb-defile', c.scrollHeight > c.clientHeight + 1);
    }

    ouvrir() {
      if (this.dialog.open) return;
      this.construire();
      const actif = document.activeElement;
      if (actif && actif.tagName === 'INPUT' && this.cadreur.contains(actif)) actif.blur(); // le clavier se ferme avant la montée du panneau
      this.bouton.focus({ preventScroll: true }); // le dialog rendra le focus au bouton, jamais à un champ
      const lu = this.cadreur.mesuresSaisies();
      const L = mesure(lu.L);
      const H = mesure(lu.H);
      const borne = (v, [min, max]) => Math.min(Math.max(v, min), max);
      this.choisi = { L: L !== null, H: H !== null }; // un champ vide ou refusé : sa paire montre l'exemple, en retrait
      this.depasse = H !== null && H > this.limites.H[1];
      this.buteH = false;
      for (const d of ['L', 'H']) this.el.dims[d].dataset.etat = this.choisi[d] ? 'choisi' : 'exemple';
      this.dialog.showModal();
      document.documentElement.classList.add('mb-molettes-ouvert');
      this.el.limite.hidden = true; // repartir d'un corps en haut : la ligne des 4 m se remontre si besoin (montrerLimite)
      this.el.corps.scrollTop = 0;
      this.ajuster();
      this.poser('L', borne(L ?? lu.exempleL, this.limites.L));
      this.poser('H', borne(H ?? lu.exempleH, this.limites.H));
      this.majTotal();
      if (this.ro) [this.el.corps, ...this.el.corps.children].forEach((el) => this.ro.observe(el));
      this.el.titre.focus({ preventScroll: true });
    }

    appliquer() {
      this.toutes().forEach((m) => m.lacherTout()); // un doigt encore posé : on pose d'abord, puis on lit les cibles
      const L = this.choisi.L ? this.total('L') : null; // jamais l'exemple d'une dimension que le client n'a pas touchée
      const H = this.choisi.H ? this.total('H') : null;
      this.dialog.close();
      this.cadreur.poserMesures(L, H); // même tâche : prix, cadre et lés n'attendent aucune animation
    }

    apresFermeture() { // événement close : croix, Échap, validation
      document.documentElement.classList.remove('mb-molettes-ouvert');
      if (this.ro) this.ro.disconnect();
      this.toutes().forEach((m) => { m.lacherTout(); m.anim = null; m.pos = m.cible; m.peindre(); });
      boucle.vider();
      this.dialog.classList.remove('mb-defile');
      this.bouton.classList.toggle('mb-au-doigt', this.auDoigt);
      this.auDoigt = false;
      if (this.bouton.isConnected) this.bouton.focus({ preventScroll: true });
    }
  }

  customElements.define('mb-molettes', MbMolettes);
})();
